import ky from 'ky';

const mergeConfig = (defaults, config) => ({
    ...defaults,
    ...config,
    headers: {
        ...defaults?.headers,
        ...config?.headers
    }
});

/**
 * Firefox and Safari haven't supported file upload progress yet
 * https://developer.mozilla.org/en-US/docs/Web/API/Window/fetch#options
 * #Send ReadableStream in body
 * @returns boolean
 */
function isUploadProgressAvailable() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('firefox')) {
        return false;
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
        // Note: Chrome's user agent also includes "safari", so it must be excluded.
        return false;
    }
    return true;
}

/**
 * Create ky instance that has similar API to axios
 * @param {import('./AxiosKy.d.ts').AxiosOptions} defaultConfig
 * @returns
 */
export function createInstance(defaultConfig = {}) {
    let kyInstance = ky.create(defaultConfig);

    async function request(config) {
        // ky doesn't accept url to start with /
        if (config?.url.startsWith('/')) {
            config.url = config.url.slice(1);
        }

        let mergedConfig = mergeConfig(defaultConfig, config);

        // remove undefined params
        try {
            if (mergedConfig.params) {
                Object.keys(mergedConfig.params).forEach((key) => {
                    if (mergedConfig.params[key] === undefined || mergedConfig.params[key] === null) {
                        delete mergedConfig.params[key];
                    }
                });
            }
        } catch (e) {
            console.error('Error deleting undefined params', e);
        }

        const kyOptions = {
            method: mergedConfig.method,
            headers: mergedConfig.headers,
            searchParams: mergedConfig.params,
            timeout: mergedConfig.timeout,
            stringifyJson: mergedConfig.stringifyJson,
            retry: 0
        };

        // set data to body / json depends on data type
        if (mergedConfig.data) {
            if (mergedConfig.data instanceof FormData || mergedConfig.data instanceof URLSearchParams) {
                kyOptions.body = mergedConfig.data;
            } else {
                const headers = mergedConfig?.headers || {};
                const contentType = Object.keys(headers).find((h) => h.toLowerCase() === 'content-type');
                const isJSON = contentType && headers[contentType]?.includes('application/json');

                // content-type is not set, use json for POST, PUT, PATCH, DELETE requests
                if (!contentType && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(mergedConfig.method.toUpperCase())) {
                    kyOptions.json = mergedConfig.data;
                } else if (isJSON) {
                    kyOptions.json = mergedConfig.data;
                } else {
                    kyOptions.body = mergedConfig.data;
                }
            }
        }

        // Handling onUploadProgress
        if (mergedConfig.onUploadProgress && isUploadProgressAvailable()) {
            kyOptions.onUploadProgress = mergedConfig.onUploadProgress;
        }

        try {
            const response = await kyInstance(config.url, kyOptions);

            let responseData = await parseResponse(response, config.parseText);

            // Create axios-like response: https://axios-http.com/docs/res_schema
            const axiosResponse = {
                data: responseData,
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries()),
                config: mergedConfig
            };

            return axiosResponse;
        } catch (error) {
            let responseData = await parseResponse(error.response);
            const axiosError = createAxiosError(error, responseData, mergedConfig);
            throw axiosError;
        }
    }

    /**
     * Full config options
     * @param {import('./AxiosKy.d.ts').AxiosOptions} config
     */
    function instance(config) {
        return request(config);
    }

    /**
     * @param {string} url
     * @param {import('./AxiosKy.d.ts').AxiosOptions} config
     */
    instance.get = (url, config = {}) => {
        return request({ method: 'GET', url, ...config });
    };

    /**
     * @param {string} url
     * @param {import('./AxiosKy.d.ts').AxiosOptions} config
     */
    instance.head = (url, config = {}) => {
        return request({ method: 'HEAD', url, ...config });
    };

    /**
     * @param {string} url
     * @param {Object} data
     * @param {import('./AxiosKy.d.ts').AxiosOptions} config
     */
    instance.post = (url, data, config = {}) => {
        return request({ method: 'POST', url, data, ...config });
    };

    /**
     * @param {string} url
     * @param {Object} data
     * @param {import('./AxiosKy.d.ts').AxiosOptions} config
     */
    instance.put = (url, data, config = {}) => {
        return request({ method: 'PUT', url, data, ...config });
    };

    /**
     * @param {string} url
     * @param {Object} data
     * @param {import('./AxiosKy.d.ts').AxiosOptions} config
     */
    instance.patch = (url, data, config = {}) => {
        return request({ method: 'PATCH', url, data, ...config });
    };

    /**
     * @param {string} url
     * @param {import('./AxiosKy.d.ts').AxiosOptions} config
     */
    instance.delete = (url, config = {}) => {
        return request({ method: 'DELETE', url, ...config });
    };

    return instance;
}

function isEmptyBody(response) {
    return !response || response.headers?.get('content-length') === '0';
}

function createAxiosError(error, responseData, config) {
    const axiosError = {
        message: error.message,
        response: { data: responseData, status: error?.response?.status },
        config: config
    };
    return axiosError;
}

async function parseResponse(response, parseText = false) {
    let responseData;
    if (!isEmptyBody(response)) {
        try {
            // ignore content-type, always parse as text
            if (parseText) {
                responseData = await response.text();
                return responseData;
            }

            // parse body using content-type
            const contentType = response?.headers.get('content-type');
            if (contentType?.includes('application/json')) {
                responseData = await response.json();
            } else if (contentType?.includes('multipart/form-data')) {
                responseData = await response.formData();
            } else if (
                contentType?.includes('application/octet-stream') ||
                contentType?.includes('application/pdf') ||
                contentType?.includes('application/zip') ||
                contentType?.includes('image/') ||
                contentType?.includes('audio/') ||
                contentType?.includes('video/')
            ) {
                responseData = await response.blob();
            } else {
                responseData = await response.text();
            }
        } catch (jsonError) {
            // can't parse json, likely payload issue
            console.error('Cannot parse', jsonError);
        }
    }
    return responseData;
}
